import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  HiOutlineCalendar, 
  HiOutlineClock, 
  HiOutlineLocationMarker, 
  HiOutlineUserGroup,
  HiOutlineTag
} from 'react-icons/hi';
import './Eventos.css';

const CMS_API_URL = process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api';
const MEDIA_URL = (process.env.REACT_APP_CMS_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');

export default function Eventos() {
  const [eventos, setEventos] = useState([]);
  const [eventoActivo, setEventoActivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const cargarEventos = useCallback(async () => {
    try {
      const response = await axios.get(`${CMS_API_URL}/events?active=true&upcoming=false&lang=${lang}`);
      const eventosData = response.data.events || [];
      setEventos(eventosData);
    } catch (error) {
      console.error('Error al cargar eventos:', error);
      // Si falla, mostrar array vacío
      setEventos([]);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    cargarEventos();
  }, [cargarEventos]);

  const getTituloEvento = (evento) => {
    return lang === 'en' ? evento.title_en : evento.title_es;
  };

  const getDescripcionEvento = (evento) => {
    return lang === 'en' ? evento.description_en : evento.description_es;
  };

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString(lang === 'en' ? 'en-US' : 'es-MX', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Parsear fecha correctamente para evitar problemas de zona horaria
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    // Extraer solo la parte de la fecha (antes de 'T' o tomar todo si no hay 'T')
    const fechaSolo = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = fechaSolo.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
  };

  const getEstadoEvento = (evento) => {
    if (evento.status === 'cancelled') {
      return {
        texto: lang === 'en' ? 'Cancelled' : 'Cancelado',
        clase: 'estado-cancelado'
      };
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaEvento = parseLocalDate(evento.event_date);
    const fechaFin = evento.end_date ? parseLocalDate(evento.end_date) : fechaEvento;

    console.log('DEBUG Evento:', {
      titulo: evento.title_es,
      hoy: hoy.toISOString().split('T')[0],
      fechaEvento: fechaEvento.toISOString().split('T')[0],
      fechaFin: fechaFin.toISOString().split('T')[0],
      esVencido: hoy > fechaFin,
      status: evento.status
    });

    // Si el evento ya pasó su fecha (vencido)
    if (hoy > fechaFin && evento.status !== 'completed') {
      return {
        texto: lang === 'en' ? 'Expired' : 'Vencido',
        clase: 'estado-vencido'
      };
    }

    // Si está marcado manualmente como completado
    if (evento.status === 'completed') {
      return {
        texto: lang === 'en' ? 'Completed' : 'Finalizado',
        clase: 'estado-finalizado'
      };
    }

    // Si está en curso
    if (evento.status === 'ongoing' || (hoy >= fechaEvento && hoy <= fechaFin)) {
      return {
        texto: lang === 'en' ? 'Ongoing' : 'En Curso',
        clase: 'estado-en-curso'
      };
    }

    // Si es próximo (futuro)
    return {
      texto: lang === 'en' ? 'Upcoming' : 'Próximo',
      clase: 'estado-proximo'
    };
  };

  const esEventoVencido = (evento) => {
    if (evento.status === 'cancelled' || evento.status === 'completed') return false;
    
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaFin = evento.end_date ? parseLocalDate(evento.end_date) : parseLocalDate(evento.event_date);
    
    return hoy > fechaFin;
  };

  if (loading) {
    return (
      <div className="eventos-section">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t("eventos.cargando")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eventos-section">
      <h2 className="titulo-eventos">{t("eventos.titulo")}</h2>
      
      {eventos.length === 0 ? (
        <div className="no-eventos">
          <p>{t("eventos.noDisponibles")}</p>
        </div>
      ) : (
        <div className="tarjetas-container">
          {eventos.map(evento => {
            const estado = getEstadoEvento(evento);
            const esVencido = esEventoVencido(evento);
            const esCancelado = evento.status === 'cancelled';
            const tieneOverlay = esCancelado || esVencido;
            
            return (
              <div className="tarjeta-evento" key={evento.id}>
                <div className="evento-imagen-wrapper">
                  {evento.featured_image && (
                    <img 
                      src={`${MEDIA_URL}${evento.featured_image}`} 
                      alt={getTituloEvento(evento)}
                      className={tieneOverlay ? (esCancelado ? 'imagen-cancelada' : 'imagen-desaturada') : ''}
                      onError={(e) => {
                        e.target.src = '/imagenes/Eventos/EventoEj.png';
                      }}
                    />
                  )}
                  {esCancelado && (
                    <div className="overlay-cancelado">
                      <span className="texto-cancelado">{estado.texto}</span>
                    </div>
                  )}
                  {esVencido && !esCancelado && (
                    <div className="overlay-vencido">
                      <span className="texto-vencido">{estado.texto}</span>
                    </div>
                  )}
                  <span className={`badge-estado ${estado.clase}`}>{estado.texto}</span>
                </div>
                <div className="evento-info">
                  <h3>{getTituloEvento(evento)}</h3>
                  <p className="evento-fecha"><HiOutlineCalendar style={{display: 'inline', marginRight: '6px'}} /> {formatFecha(evento.event_date)}</p>
                  {evento.event_time && (
                    <p className="evento-hora"><HiOutlineClock style={{display: 'inline', marginRight: '6px'}} /> {evento.event_time}</p>
                  )}
                  {evento.location_es && (
                    <p className="evento-ubicacion"><HiOutlineLocationMarker style={{display: 'inline', marginRight: '6px'}} /> {lang === 'en' ? evento.location_en : evento.location_es}</p>
                  )}
                  <button onClick={() => setEventoActivo(evento)}>{t("eventos.ver")}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {eventoActivo && (
        <div className="modal-overlay" onClick={() => setEventoActivo(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-imagen-wrapper">
              {eventoActivo.featured_image && (
                <img 
                  src={`${MEDIA_URL}${eventoActivo.featured_image}`} 
                  alt={getTituloEvento(eventoActivo)}
                  className={(eventoActivo.status === 'cancelled' || esEventoVencido(eventoActivo)) ? 
                    (eventoActivo.status === 'cancelled' ? 'imagen-cancelada' : 'imagen-desaturada') : ''}
                />
              )}
              {eventoActivo.status === 'cancelled' && (
                <div className="overlay-cancelado-modal">
                  <span className="texto-cancelado-grande">{lang === 'en' ? 'CANCELLED' : 'CANCELADO'}</span>
                </div>
              )}
              {esEventoVencido(eventoActivo) && eventoActivo.status !== 'cancelled' && (
                <div className="overlay-vencido-modal">
                  <span className="texto-vencido-grande">{lang === 'en' ? 'EXPIRED' : 'VENCIDO'}</span>
                </div>
              )}
              <span className={`badge-estado-modal ${getEstadoEvento(eventoActivo).clase}`}>
                {getEstadoEvento(eventoActivo).texto}
              </span>
            </div>
            <h3>{getTituloEvento(eventoActivo)}</h3>
            <p className="modal-descripcion">{getDescripcionEvento(eventoActivo)}</p>
            <div className="modal-detalles">
              <p className="modal-fecha"><HiOutlineCalendar style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("eventos.fecha")}:</strong> {formatFecha(eventoActivo.event_date)}</p>
              {eventoActivo.event_time && (
                <p className="modal-hora"><HiOutlineClock style={{display: 'inline', marginRight: '6px'}} /> <strong>{lang === 'en' ? 'Time' : 'Hora'}:</strong> {eventoActivo.event_time}</p>
              )}
              {eventoActivo.end_date && (
                <p className="modal-fecha-fin"><HiOutlineCalendar style={{display: 'inline', marginRight: '6px'}} /> <strong>{lang === 'en' ? 'End Date' : 'Fecha Fin'}:</strong> {formatFecha(eventoActivo.end_date)}</p>
              )}
              {eventoActivo.location_es && (
                <p className="modal-ubicacion">
                  <HiOutlineLocationMarker style={{display: 'inline', marginRight: '6px'}} /> <strong>{t("eventos.ubicacion")}:</strong> {lang === 'en' ? eventoActivo.location_en : eventoActivo.location_es}
                </p>
              )}
              {eventoActivo.max_participants && (
                <p className="modal-participantes"><HiOutlineUserGroup style={{display: 'inline', marginRight: '6px'}} /> <strong>{lang === 'en' ? 'Max Participants' : 'Máximo de Participantes'}:</strong> {eventoActivo.max_participants}</p>
              )}
              {eventoActivo.category && (
                <p className="modal-categoria"><HiOutlineTag style={{display: 'inline', marginRight: '6px'}} /> <strong>{lang === 'en' ? 'Category' : 'Categoría'}:</strong> {eventoActivo.category}</p>
              )}
            </div>
            {eventoActivo.registration_url && (
              <a 
                href={eventoActivo.registration_url} 
                className="boton-registro"
                target="_blank"
                rel="noopener noreferrer"
              >
                {lang === 'en' ? 'Register' : 'Inscribirse'}
              </a>
            )}
            {eventoActivo.document_url && (
              <a 
                href={`${MEDIA_URL}${eventoActivo.document_url}`} 
                download 
                className="boton-descargar"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("eventos.descargar")}
              </a>
            )}
            <button className="cerrar" onClick={() => setEventoActivo(null)}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}